import { createContext, FC, ReactNode, useContext } from 'react';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../Typography';
import { CloseRounded } from '../svgs';

// ─── Context ──────────────────────────────────────────────────────────────────

type NotificationVariant = 'grey' | 'gold' | 'red';

interface NotificationContextValue {
  variant: NotificationVariant;
}

const NotificationContext = createContext<NotificationContextValue>({ variant: 'grey' });
const useNotification = () => useContext(NotificationContext);

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NotificationProps {
  /** Background colour theme */
  variant?: NotificationVariant;
  children: ReactNode;
  className?: string;
}

export interface NotificationBadgeProps {
  /** Leading icon / logo slot */
  children: ReactNode;
  className?: string;
}

export interface NotificationBodyProps {
  /** flex-col wrapper for Heading + Content */
  children: ReactNode;
  className?: string;
}

export interface NotificationHeadingProps {
  children: ReactNode;
  className?: string;
}

export interface NotificationContentProps {
  children: ReactNode;
  className?: string;
}

export interface NotificationButtonProps {
  /**
   * primary (default) — filled white on gold/red; blue text-link on grey
   * ghost            — outlined
   */
  variant?: 'primary' | 'ghost';
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

export interface NotificationCloseProps {
  /** Called when the × dismiss button is pressed */
  onHandleClose: () => void;
  className?: string;
}

export interface NotificationActionsProps {
  /**
   * Right-column slot used in Games (red) mobile layout.
   * Stacks children vertically with close at the top and button at the bottom.
   * On desktop it switches to a horizontal row to match the inline layout.
   */
  children: ReactNode;
  className?: string;
}

// ─── Colour maps ──────────────────────────────────────────────────────────────

const bgMap: Record<NotificationVariant, string> = {
  grey: 'bg-[--notification-bg-grey]',
  gold: 'bg-[--notification-bg-gold]',
  red: 'bg-[--notification-bg-red]',
};

const fgMap: Record<NotificationVariant, string> = {
  grey: 'text-[--notification-fg-dark]',
  gold: 'text-[--notification-fg-dark]',
  red: 'text-[--notification-fg-light]',
};

// ─── Badge ────────────────────────────────────────────────────────────────────

/**
 * Notification.Badge — leading icon / logo slot.
 * No fixed size — the child content determines the dimensions.
 * (Booster: 24×24 mobile / 28×28 desktop via the asset itself.
 *  Games desktop: 63×40 logo asset.)
 */
const Badge: FC<NotificationBadgeProps> = ({ children, className }) => (
  <div className={twMerge('flex-shrink-0 flex items-center justify-center', className)}>
    {children}
  </div>
);

// ─── Body ─────────────────────────────────────────────────────────────────────

/**
 * Notification.Body — flex-1 column wrapper for Heading + Content.
 */
const Body: FC<NotificationBodyProps> = ({ children, className }) => (
  <div className={twMerge('flex flex-col gap-[--padding-3xs] flex-1 min-w-0', className)}>
    {children}
  </div>
);

// ─── Heading ──────────────────────────────────────────────────────────────────

/**
 * Notification.Heading — bold title.
 * subheadline030 (16px/20px) mobile → subheadline040 (18px/24px) desktop
 */
const Heading: FC<NotificationHeadingProps> = ({ children, className }) => {
  const { variant } = useNotification();
  return (
    <Typography
      as="p"
      variant="subheadline030"
      className={twMerge('md:text-18 md:leading-24', fgMap[variant], className)}
    >
      {children}
    </Typography>
  );
};

// ─── Content ──────────────────────────────────────────────────────────────────

/**
 * Notification.Content — supporting body copy.
 * paragraph010 (12px/16px) on all breakpoints.
 */
const Content: FC<NotificationContentProps> = ({ children, className }) => {
  const { variant } = useNotification();
  return (
    <Typography as="p" variant="paragraph010" className={twMerge(fgMap[variant], className)}>
      {children}
    </Typography>
  );
};

// ─── Button ───────────────────────────────────────────────────────────────────

/**
 * Notification.Button — CTA button whose style adapts to the variant:
 *
 * grey  → blue text-link (no background, no border)
 * gold  → white solid button, dark text
 * red   → white solid button, dark text
 *
 * ghost on gold/red → outlined white/dark border instead of filled.
 */
const NotificationButton: FC<NotificationButtonProps> = ({
  variant: btnVariant = 'primary',
  children,
  className,
  onClick,
}) => {
  const { variant } = useNotification();

  // Grey: always a blue text-link regardless of btnVariant
  if (variant === 'grey') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={twMerge(
          'flex-shrink-0 bg-transparent text-[--notification-btn-primary-bg]',
          'text-14 leading-20 font-bold py-[8px] cursor-pointer whitespace-nowrap',
          className
        )}
      >
        {children}
      </button>
    );
  }

  // Gold / Red: solid white (primary) or outlined (ghost)
  const isRed = variant === 'red';

  const primaryCls = 'bg-white border border-transparent text-[--notification-fg-dark]';
  const ghostCls = isRed
    ? 'bg-transparent border border-white text-[--notification-fg-light]'
    : 'bg-transparent border border-[--notification-fg-dark] text-[--notification-fg-dark]';

  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge(
        'flex-shrink-0 rounded-[--corner-xs]',
        'p-[10px]', // Figma: p-10 all sides → 40px height
        'text-14 leading-20 font-bold cursor-pointer whitespace-nowrap',
        btnVariant === 'primary' ? primaryCls : ghostCls,
        className
      )}
    >
      {children}
    </button>
  );
};

// ─── Close ────────────────────────────────────────────────────────────────────

/**
 * Notification.Close — × dismiss button.
 */
const Close: FC<NotificationCloseProps> = ({ onHandleClose, className }) => {
  const { variant } = useNotification();
  return (
    <button
      type="button"
      onClick={onHandleClose}
      aria-label="Dismiss notification"
      className={twMerge(
        'flex-shrink-0 flex items-start justify-center size-6 cursor-pointer',
        fgMap[variant],
        className
      )}
    >
      <CloseRounded className="size-4" />
    </button>
  );
};

// ─── Sun Swirl (Games decoration) ────────────────────────────────────────────

/**
 * Decorative background for the red (Games) variant.
 * Concentric circle arcs positioned below the notification — only their tops
 * are visible, creating the curved wave-band effect from the Figma design.
 * Clipped by `overflow-hidden` on the parent container.
 */
const SunSwirl: FC = () => (
  <div
    aria-hidden="true"
    className={[
      'absolute pointer-events-none opacity-[0.08]',
      // Small: 700 × 700 circle, mostly below the component
      'bottom-[-70px] left-[calc(50%-140px)] size-[700px]',
      // Large: 900 × 900 circle, centred horizontally
      'md:bottom-[-150px] md:left-1/2 md:-translate-x-1/2 md:size-[900px]',
    ].join(' ')}
  >
    <svg viewBox="0 0 100 100" className="w-full h-full" fill="none">
      {[48, 42, 36, 30, 24, 18, 12, 6].map(r => (
        <circle key={r} cx="50" cy="50" r={r} stroke="white" strokeWidth="2.5" />
      ))}
    </svg>
  </div>
);

// ─── Actions ─────────────────────────────────────────────────────────────────

/**
 * Notification.Actions — right-column wrapper for the Games (red) mobile layout.
 *
 * Mobile : `flex-col self-stretch` — stretches to the full row height so the
 *          column is as tall as the Body. Pass `className="mb-auto"` to
 *          `Notification.Close` to anchor it at the top and push
 *          `Notification.Button` to the bottom.
 * Desktop: flips to an inline `flex-row`.
 *
 * @example
 * <Notification variant="red">
 *   <Notification.Body>…</Notification.Body>
 *   <Notification.Actions>
 *     <Notification.Close onHandleClose={() => {}} className="mb-auto md:mb-0" />
 *     <Notification.Button>Play Now</Notification.Button>
 *   </Notification.Actions>
 * </Notification>
 */
const Actions: FC<NotificationActionsProps> = ({ children, className }) => (
  <div
    className={twMerge(
      // Mobile: full-height column, children align to the end (right edge)
      'flex-shrink-0 flex flex-col self-stretch items-end',
      // Desktop: inline horizontal row
      'md:flex-row md:items-center md:gap-[--padding-md] md:self-auto',
      className
    )}
  >
    {children}
  </div>
);

// ─── Compound type ────────────────────────────────────────────────────────────

type NotificationComponent = FC<NotificationProps> & {
  Badge: typeof Badge;
  Body: typeof Body;
  Heading: typeof Heading;
  Content: typeof Content;
  Button: typeof NotificationButton;
  Close: typeof Close;
  Actions: typeof Actions;
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * Notification — horizontal flex banner.
 *
 * Variants
 * - `grey`  In-Page: text body + blue text-link CTA
 * - `gold`  Booster: icon + heading (+ body on desktop) + white button
 * - `red`   Games:   brand logo + heading + body + white button + close
 *
 * @example — In-Page
 * <Notification variant="grey">
 *   <Notification.Body>
 *     <Notification.Content>Message text.</Notification.Content>
 *   </Notification.Body>
 *   <Notification.Button>Learn More</Notification.Button>
 * </Notification>
 *
 * @example — Games (with mobile stacked close/button layout)
 * <Notification variant="red">
 *   <Notification.Badge><Logo /></Notification.Badge>
 *   <Notification.Body>
 *     <Notification.Heading>Score Predictor</Notification.Heading>
 *     <Notification.Content>Description here.</Notification.Content>
 *   </Notification.Body>
 *   <Notification.Actions>
 *     <Notification.Close onHandleClose={() => {}} />
 *     <Notification.Button>Play Now</Notification.Button>
 *   </Notification.Actions>
 * </Notification>
 */
export const Notification: NotificationComponent = ({ variant = 'grey', children, className }) => (
  <NotificationContext.Provider value={{ variant }}>
    {/* Outer shell — owns the background, border-radius, overflow clip, and decoration */}
    <div
      className={twMerge(
        'relative overflow-hidden',
        'rounded-[--corner-xs] md:rounded-[--corner-sm] w-full',
        bgMap[variant],
        className
      )}
    >
      {/* Decorative sun swirl for Games (red) variant */}
      {variant === 'red' && <SunSwirl />}

      {/* Content row — sits above the decoration */}
      <div
        className={twMerge(
          'relative flex items-center gap-[--padding-sm] md:gap-[--padding-md]',
          'px-[--padding-sm] py-[--padding-xs] md:px-[--padding-md] md:py-[--padding-sm]'
        )}
      >
        {children}
      </div>
    </div>
  </NotificationContext.Provider>
);

Notification.Badge = Badge;
Notification.Body = Body;
Notification.Heading = Heading;
Notification.Content = Content;
Notification.Button = NotificationButton;
Notification.Close = Close;
Notification.Actions = Actions;
